// --- React
import React from 'react';
// --- Redux
import {useSelector} from 'react-redux';
// --- UI Libraries
import {Col, Row} from 'antd';
import Typography from '@dtci/spellbook/Typography';
import Button from '@dtci/spellbook/Button';
// --- Components
import {Pagination} from '../../components/Pagination';
import {ContactsFilterForm} from './components/filter-form';
import {ContactsGrid} from './components/grid';
import {TwoRowsLayout} from './layout/two-row';
// --- Custom Hooks
import {useLoading} from '../../utils/hooks/useLoading';
import {useContactsActions} from '../../store/contacts/useContacts';
import {useContactsDialog} from '../../components/dialogs/dialogs/contacts';
// --- Utilities
import {getItemRange} from '../../utils/pagination';
import {filterCriteria} from '../../utils/filterCriteria';
import {exportToCsv} from '../../utils/exportToCsv';
// --- API
import {datastageApi} from '../../api';
import {tracker} from '../../analytics/snowplow';
import { getCurrentUser } from '../../utils/localStorageService';
/**
 * ContactsPage Component
 *
 * This component displays a paginated and filterable list of contacts.
 * It includes filtering, pagination, data fetching, and export functionality.
 *
 * @component
 * @returns {JSX.Element} The ContactsPage component.
 */
export const ContactsPage = () => {
    React.useEffect(() => {
        var currentUser = getCurrentUser();        
        tracker.setUserId(currentUser);
        tracker.trackPageView({ title: 'Contacts' });
    }, []);
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {updateContacts} = useContactsActions();
    const {totalPages, currentPage, filterForm, contactSort, contactRange, itemsPerPage, totalItems} =
        useSelector((s) => s.contacts);
    const {openContactsDialog} = useContactsDialog();

    // Handle pagination changes
    const handleChange = async (pageNumber) => {
        if (pageNumber === currentPage) return; // Avoid unnecessary API calls for the same page

        try {
            setLoadingTrue();

            // Calculate the range for the next page
            const nextPageRange = getItemRange(pageNumber, itemsPerPage, totalItems);

            // Fetch contacts for the selected page
            const data = await datastageApi.contacts(
                filterCriteria(filterForm, '', 'contacts'),
                nextPageRange,
                contactSort,
            );

            // Update store with the new contacts data
            updateContacts({
                contacts: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber,
                contactRange: nextPageRange,
            });
        } catch (error) {
            console.error('Error handling page change:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Refresh the contacts list
    const handleRefresh = async () => {
        try {
            setLoadingTrue();

            // Fetch contacts based on the current filter criteria
            const data = await datastageApi.contacts(
                filterCriteria(filterForm, '', 'contacts'),
                contactRange,
                contactSort,
            );

            // Update store with the refreshed contacts data
            updateContacts({
                contacts: data?.content,
                totalItems: data?.totalElements,
                totalPages: data?.totalPages,
                currentPage: data?.pageable?.pageNumber,
            });
        } catch (error) {
            console.error('Error refreshing contacts:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Export the contacts data to a CSV file
    const handleExport = async () => {
        try {
            setLoadingTrue();

            // Fetch all contacts for export
            const data = await datastageApi.contacts(
                filterCriteria(filterForm, '', 'contacts'),
                '0,999',
                contactSort,
            );

            // Export the data to a CSV file
            exportToCsv(data?.content, 'contacts');
        } catch (error) {
            console.error('Error exporting contacts:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Opens dialog to create a new contact
    const handleCreateContact = () => {
        openContactsDialog({}, 'Create Contact', async ({formData}) => {
            try {
                setLoadingTrue();

                // Create a new contact
                await datastageApi.createContact(formData);

                // Refresh the contacts list
                const data = await datastageApi.contacts(
                    filterCriteria(filterForm, '', 'contacts'),
                    contactRange,
                    contactSort,
                );

                // Update store with the new contacts data
                updateContacts({
                    contacts: data?.content,
                    totalPages: data?.totalPages,
                    totalItems: data?.totalElements,
                    currentPage: data?.pageable?.pageNumber,
                });
            } catch (error) {
                console.error('Error creating contact:', error);
            } finally {
                setLoadingFalse();
            }
        });
    };

    return (
        <>
            {/* Header Row with Title and Action Buttons */}
            <Row style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20}}>
                <Col>
                    <Typography variant="T50" style={{marginLeft: 20}}>
                        Contacts
                    </Typography>
                </Col>
                <Col style={{display: 'flex', gap: 8}}>
                    <Button
                        iconType="fa-solid fa-plus"
                        labelText="Create"
                        styleType="link"
                        onClick={handleCreateContact}
                    >
                        Create
                    </Button>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>

            {/* Layout with Filter Form and Contacts Grid */}
            <TwoRowsLayout>
                <main data-position="top">
                    <ContactsFilterForm/>
                </main>
                <main data-position="bottom">
                    <ContactsGrid/>
                </main>
            </TwoRowsLayout>

            {/* Pagination Controls */}
            <Pagination
                pageCount={totalPages}
                onPageChange={(e) => handleChange(e.selected)}
                previousLabel="←"
                nextLabel="→"
                forcePage={currentPage}
            />
        </>
    );
};
